import { useForm, Controller } from 'react-hook-form';
import { Button } from 'primereact/button';
import TextSnippetWrapper from 'components/TextSnippetWrapper';

/**
 * Form component for adding or editing comments using React Hook Form
 *
 * @param {Object} props - The component props
 * @param {Function} props.onSubmit - Function to call when the form is submitted
 * @param {boolean} props.isSubmitting - Whether the form is currently submitting
 * @param {boolean} props.isEditing - Whether the form is in edit mode
 * @param {string} props.defaultValue - Default text value for the form
 * @param {string} props.buttonLabel - Label for the submit button
 * @returns {JSX.Element} The rendered form component
 */
export const CommentForm = ({ onSubmit, isSubmitting, isEditing = false, defaultValue = '', buttonLabel = 'Add Comment' }) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      comment: defaultValue,
    },
  });

  /**
   * Handles form submission and resets the form if not in edit mode
   *
   * @param {Object} data - The form data
   * @param {string} data.comment - The comment text
   */
  const submitForm = (data) => {
    onSubmit(data.comment);
    if (!isEditing) {
      reset({ comment: '' });
    }
  };

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <Controller
        name="comment"
        control={control}
        rules={{ required: 'Comment cannot be empty' }}
        render={({ field }) => (
          <TextSnippetWrapper type="textarea" rows="3" className="w-100 input-shadow" placeholder="Add a comment..." {...field} />
        )}
      />
      {errors.comment && <small className="p-error">{errors.comment.message}</small>}
      <div className="d-flex justify-content-end mt-2">
        <Button
          className="p-button-secondary"
          type="submit"
          disabled={isSubmitting}
          loading={isSubmitting}
          loadingIcon="pi pi-spin pi-spinner"
          label={buttonLabel}
        />
      </div>
    </form>
  );
};
